import { useMutation } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useEffect, useState } from "react";

import { ApiError, verifyReferralCode } from "../../api/client";
import { RequestAccessShell } from "../../components/RequestAccessShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { FormError } from "@/components/ui/form-error";

// CHP-809: re-read the code the wizard stashed in step 1 so navigating Back
// from the Details step restores the entered code rather than dropping the
// user on an empty field. Mirrors details.tsx's readStashedState() — same key,
// same shape, same fail-open-to-empty parsing.
function readStashedCode(): string {
  try {
    const raw = sessionStorage.getItem("prm-request-access");
    if (!raw) return "";
    const parsed = JSON.parse(raw) as { referralCode?: unknown };
    return typeof parsed?.referralCode === "string" ? parsed.referralCode : "";
  } catch {
    return "";
  }
}

export function RequestAccessCodeStep() {
  const navigate = useNavigate();
  // Seed from the stash on mount so Back from step 2 restores the entered code.
  const [code, setCode] = useState(readStashedCode);
  const [error, setError] = useState<string | null>(null);
  // CHP-809: on a valid code, briefly confirm what the user is joining (the
  // verify response's human-readable `label`) before advancing — so a valid
  // code is visibly distinct from an invalid-but-slow one, and the user is
  // reassured they entered the right code.
  const [confirmation, setConfirmation] = useState<string | null>(null);

  // CHP-808: the Details step routes back here when the referral code went
  // stale/depleted between verify and submit (a 400/422 with no field on that
  // form to surface). Show the reason once, then clear it so a manual revisit
  // is clean.
  useEffect(() => {
    const stashed = sessionStorage.getItem("prm-request-access-error");
    if (stashed) {
      setError(stashed);
      sessionStorage.removeItem("prm-request-access-error");
    }
  }, []);

  const verify = useMutation({
    mutationFn: (raw: string) => verifyReferralCode(raw),
    onSuccess: (resp) => {
      if (!resp.valid) {
        setError("That code isn't valid. Check with the person who invited you.");
        return;
      }
      sessionStorage.setItem(
        "prm-request-access",
        JSON.stringify({
          referralCode: code.trim().toUpperCase(),
          label: resp.label,
        }),
      );
      // Surface a brief confirmation (with the label when present) before
      // advancing, so a valid verify is visibly distinct from an invalid one.
      setConfirmation(resp.label ? `Verified — you're joining ${resp.label}.` : "Code verified.");
      window.setTimeout(() => {
        void navigate({ to: "/request-access/details" });
      }, 900);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 429) {
        setError("Too many attempts. Wait a minute and try again.");
        return;
      }
      setError("Couldn't verify the code. Try again.");
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setConfirmation(null);
    if (code.trim().length < 4) {
      setError("Enter the code your contact gave you.");
      return;
    }
    verify.mutate(code);
  }

  // Stay in the "working" state through the confirmation pause so the user
  // can't double-submit while the brief success line is shown.
  const busy = verify.isPending || confirmation !== null;

  return (
    <RequestAccessShell step={1}>
      <p className="text-sm text-muted-foreground">
        Enter the referral code provided by your ResetData contact.
      </p>
      <form onSubmit={onSubmit} aria-busy={busy} noValidate className="space-y-4">
        <div className="space-y-2">
          <Label
            htmlFor="referral-code"
            className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground"
          >
            Referral code
          </Label>
          <Input
            id="referral-code"
            autoFocus
            aria-required="true"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="e.g. ABCDE234"
            className="font-mono tracking-widest uppercase"
          />
        </div>
        {error && <FormError>{error}</FormError>}
        {confirmation && (
          <p role="status" aria-live="polite" className="text-sm font-medium text-success">
            {confirmation}
          </p>
        )}
        <Button type="submit" disabled={busy} size="lg" className="w-full">
          {busy ? "Verifying…" : "Continue"}
        </Button>
      </form>
      <p className="text-center text-sm text-muted-foreground">
        Already have an account?{" "}
        <Link to="/login" className="font-medium text-primary hover:underline">
          Sign in instead
        </Link>
      </p>
    </RequestAccessShell>
  );
}
